import getConfig from 'next/config'
import { useEffect, useState, useContext } from 'react'

import { SamlLoginOptionResponse } from '@src/api/generated/iam'
import { EXTERNAL_LINKS, PATHS, L_STORAGE_KEYS } from '@src/consts'
import {
  useCustomSnackbar,
  useFetchUserMe,
  useFetchSsoJwt,
  homePagePath,
  useCustomRouter,
  useLoginWithEmailAndPassword,
  useSaveSamlRedirectUrl,
  useFetchLoginOptions,
} from '@src/hooks'
import { TfaTokenContext } from '@src/pages/_app.page'
import { getUser, lStorage, toAuthenticationType } from '@src/utils'
import { toBooleanFromStr } from '@src/utils/to-boolean-from-str/to-boolean-from-str'

import { STAGING_API_ENDPOINT } from './const'
import { UseSignin } from './type'

export const useSignin = ({
  setLoginErrors,
  getEmailFormValue,
  getPasswordFormValue,
  handleChangePageState,
}: UseSignin) => {
  const { router } = useCustomRouter()
  const query = router.query
  const isFailed = toBooleanFromStr(query.failed as string)

  const queryString = window.location.search
  const { openSnackbar, closeSnackbar } = useCustomSnackbar()
  const { publicRuntimeConfig } = getConfig()
  const { BASE_URL, API_ENDPOINT } = publicRuntimeConfig
  const tfaTokenContext = useContext(TfaTokenContext)

  // To be able to request the Staging API from Local to increase development efficiency
  const isConnectedLocalToStg =
    API_ENDPOINT === STAGING_API_ENDPOINT &&
    process.env.NODE_ENV !== 'production'

  const [freshdeskNonce, setFreshdeskNonce] = useState('')
  const [enabledFetchUserMe, setEnabledFetchUserMe] = useState(false)
  const [isLoadingMoveToSsoPage, setIsLoadingMoveToSsoPage] = useState(false)
  const [emailForFetchUserMe, setEmailForFetchUserMe] = useState('')

  const fetchUserMe = useFetchUserMe({ enabled: enabledFetchUserMe })
  const fetchSsoJwt = useFetchSsoJwt({ nonce: freshdeskNonce })

  const saveSamlRedirectUrlMutation = useSaveSamlRedirectUrl()
  const fetchedLoginOptions = useFetchLoginOptions({
    email: emailForFetchUserMe,
    clientType: 'web',
  })
  const rawMutatedLoginOptions = saveSamlRedirectUrlMutation.data?.data
  const rawFetchedLoginOptions = fetchedLoginOptions.data?.data
  const isLoadingFetchLoginOptions = saveSamlRedirectUrlMutation.isPending
  const loginWithEmailAndPasswordMutation = useLoginWithEmailAndPassword()
  const isLoadingLoginWithEmailAndPassword =
    loginWithEmailAndPasswordMutation.isPending

  function handleSaveRedirectUrl() {
    const { email } = getEmailFormValue()
    // When you want to connect to staging from local,
    // sending the redirectUrl of the local domain is not permitted,
    // so use the API of the GET method, which does not store the redirectUrl.
    if (isFailed || isConnectedLocalToStg) {
      if (email === emailForFetchUserMe) {
        fetchedLoginOptions.refetch().then(({ data }) => {
          const rawFetchedLoginOptions = data?.data
          if (!rawFetchedLoginOptions) return
          changeLoginPageState(rawFetchedLoginOptions)
        })
      } else {
        setEmailForFetchUserMe(email)
      }
    } else {
      const redirectUrl = BASE_URL + PATHS.INVOKE_REDIRECT_WEB + queryString
      saveSamlRedirectUrlMutation.mutate(
        {
          loginOptionForm: {
            email,
            clientType: 'web',
            redirectUrl,
          },
        },
        {
          onSuccess: (data) => changeLoginPageState(data.data),
          onError: () => {
            closeSnackbar()
            openSnackbar('ログインに失敗しました', 'error')
          },
        }
      )
    }
  }

  function changeLoginPageState(samlLoginOption: SamlLoginOptionResponse) {
    if (
      samlLoginOption?.loginOptions.includes('saml') &&
      samlLoginOption?.loginOptions.includes('id_password')
    ) {
      handleChangePageState('SSO_OR_ID_AND_PASSWORD')
    } else if (
      samlLoginOption?.loginOptions.includes('saml') &&
      samlLoginOption.samlConfiguration
    ) {
      moveToSsoPage(samlLoginOption.samlConfiguration.ssoUrl)
    } else if (samlLoginOption?.loginOptions.includes('id_password')) {
      handleChangePageState('ID_AND_PASSWORD')
    }
  }

  function moveToSsoPage(ssoUrl?: string) {
    if (
      !rawMutatedLoginOptions?.samlConfiguration &&
      !fetchedLoginOptions.data?.data.samlConfiguration &&
      !ssoUrl
    ) {
      closeSnackbar()
      openSnackbar('シングルサインオンでのログインに失敗しました', 'error')
      return
    }
    setIsLoadingMoveToSsoPage(true)
    const { email } = getEmailFormValue()
    lStorage.setItem({
      key: L_STORAGE_KEYS.MAIL_ADDRESS,
      value: email,
    })
    if (rawMutatedLoginOptions?.samlConfiguration) {
      window.location.href = rawMutatedLoginOptions?.samlConfiguration.ssoUrl
    } else if (rawFetchedLoginOptions?.samlConfiguration) {
      window.location.href = rawFetchedLoginOptions?.samlConfiguration.ssoUrl
    } else if (ssoUrl) {
      window.location.href = ssoUrl
    }
    setIsLoadingMoveToSsoPage(false)
  }

  function handleLoginWithEmailAndPassword() {
    loginWithEmailAndPasswordMutation.mutate(
      {
        loginWithPasswordForm: {
          email: getEmailFormValue().email,
          password: getPasswordFormValue().password,
        },
      },
      {
        onSuccess: async ({ data }) => {
          // To be able to request the Staging API from Local to increase development efficiency
          if (isConnectedLocalToStg && data.token) {
            lStorage.setItem({
              value: data.token,
              key: L_STORAGE_KEYS.TOKEN,
            })
          }
          if (data.tfaToken && data.tfaTokenExpiresAt) {
            tfaTokenContext?.setTfaToken(data.tfaToken)
            tfaTokenContext?.setTfaTokenExpiresAt(data.tfaTokenExpiresAt)
          }
          const refererService = query.refererService as string
          const nonce = query.nonce as string
          if (!data.twoFactorAuthRegisterStatus) {
            setEnabledFetchUserMe(true)
            if (refererService === 'freshdesk') {
              setFreshdeskNonce(nonce)
            }
          } else if (data.twoFactorAuthRegisterStatus === 'APP') {
            lStorage.setItem({
              key: L_STORAGE_KEYS.MAIL_ADDRESS,
              value: data.email,
            })
            router.push({
              pathname: PATHS.SIGNIN_TFA_APP,
              query,
            })
          } else if (data.twoFactorAuthRegisterStatus === 'SMS') {
            lStorage.setItem({
              key: L_STORAGE_KEYS.SMS_PHONE_NUMBER,
              value: data.smsPhoneNumber,
            })
            lStorage.setItem({
              key: L_STORAGE_KEYS.MAIL_ADDRESS,
              value: data.email,
            })
            router.push({
              pathname: PATHS.SIGNIN_TFA_SMS,
              query,
            })
          }
        },
        onError: (error) => {
          if (error.data.errors.errors) {
            setLoginErrors(error.data.errors.errors)
          } else if (error.data.errors.password) {
            setLoginErrors(error.data.errors.password)
          } else {
            setLoginErrors(['不明なエラーが発生しました'])
          }
        },
      }
    )
  }

  function moveToNextUrl() {
    const user = getUser()
    const backUrl = query.backUrl as string

    if (!user) return

    //TODO: When SAML information can be retrieved by user-api, add a condition whether SAML is enforced or not.
    if (user.isTempPassword) {
      router.push({
        pathname: PATHS.REGISTER_PASSWORD,
      })
    } else if (backUrl) {
      window.location.replace(backUrl)
    } else {
      homePagePath(user)
    }
  }

  useEffect(() => {
    if (fetchUserMe.isSuccess) {
      const rawUserMe = fetchUserMe.data.data
      const convertedResponse = toAuthenticationType(rawUserMe)
      lStorage.setItem({ key: 'user', value: convertedResponse })
      const refererService = query.refererService as string
      if (refererService === 'freshdesk') return
      moveToNextUrl()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchUserMe.isSuccess])

  useEffect(() => {
    if (fetchSsoJwt.isSuccess) {
      const token = fetchSsoJwt.data.data.token
      window.location.href = EXTERNAL_LINKS.FRESHDESK.LINK({
        token,
        queryString,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchSsoJwt.isSuccess])

  useEffect(() => {
    const user = getUser()
    const refererService = query.refererService as string
    const nonce = query.nonce as string
    const isSignedIn = !!user?.id && !user.isTempPassword
    if (refererService === 'freshdesk' && isSignedIn) {
      setFreshdeskNonce(nonce)
    }
  }, [query.nonce, query.refererService])

  useEffect(() => {
    if (fetchedLoginOptions.isSuccess) {
      const rawLoginOptions = fetchedLoginOptions.data.data
      changeLoginPageState(rawLoginOptions)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedLoginOptions.isSuccess])

  useEffect(() => {
    if (fetchedLoginOptions.isError) {
      closeSnackbar()
      openSnackbar('ログインに失敗しました', 'error')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedLoginOptions.isError])

  useEffect(() => {
    if (isFailed) {
      setLoginErrors(['シングルサインオンでのログインに失敗しました'])
    }
  }, [isFailed, setLoginErrors])

  return {
    handleSaveRedirectUrl,
    handleLoginWithEmailAndPassword,
    moveToSsoPage,
    isLoadingFetchLoginOptions,
    isLoadingLoginWithEmailAndPassword,
    isLoadingMoveToSsoPage,
  }
}
